import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { SVG } from "../../constants/assetLists";
import QUERY_KEYS from "../../constants/queryKeys";
import {
  handleGetOrganizationDraftsService,
  OrganizationApiLocationNode,
  OrganizationBranch,
  OrganizationLocationBase,
  OrganizationNode,
  OrganizationRoot,
  OrganizationState,
  OrganizationSubBranch,
  OrganizationTableRow,
  OrganizationsScreenTypes,
} from "../../services/organizations";

interface OrganizationsScreenContextType {
  selectedTab: OrganizationsScreenTypes;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<OrganizationsScreenTypes>
  >;
  isAllOrganizationsSelected: boolean;
  setIsAllOrganizationsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  organizations: OrganizationRoot[];
  selectedSideFilterOrganization: OrganizationNode | null;
  setSelectedSideFilterOrganization: React.Dispatch<
    React.SetStateAction<OrganizationNode | null>
  >;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedOrganizationTableRowIds: Array<string | number>;
  setSelectedOrganizationTableRowIds: React.Dispatch<
    React.SetStateAction<Array<string | number>>
  >;
  organizationRows: OrganizationTableRow[];
  isDefaultParentExpanded: boolean;
  setIsDefaultParentExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrganizationsScreenContext =
  React.createContext<OrganizationsScreenContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

const mapBaseNode = (
  node: OrganizationApiLocationNode,
  draftId: string,
  fallbackStatus: OrganizationState,
): OrganizationLocationBase => {
  const contact = node.contacts?.[0];
  const status = node.status ?? fallbackStatus;

  return {
    id: node.id ?? node._id,
    _id: node._id,
    draftId,
    name: node.name ?? "",
    icon: SVG.clientIcon,
    apiIcon: node.logo?.url || "",
    type: node.type ?? null,
    status,
    isArchived: node.isArchived,
    address: node.address ?? "",
    email: node.email || contact?.email || "",
    phone: node.phone || contact?.phone || "",
    logo: node.logo ?? null,
    mobileLogo: node.mobileLogo ?? null,
    contacts: (node.contacts ?? []).map((item) => ({
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      phone: item.phone,
      role: item.role,
      systemRole: null,
    })),
    city: node.city,
    state: node.state,
    country: node.country,
    website: node.website,
    hpNumber: node.hpNumber,
    checklistNotesReset: node.checklistNotesReset,
    operatingHours: node.operatingHours,
  };
};

const mapSubBranch = (
  node: OrganizationApiLocationNode,
  draftId: string,
  fallbackStatus: OrganizationState,
  clientId: string,
  branchId: string,
): OrganizationSubBranch => ({
  ...mapBaseNode(node, draftId, fallbackStatus),
  icon: SVG.taskIcon,
  isClient: false,
  isBranch: false,
  isSubBranch: true,
  clientId,
  branchId,
});

const mapBranch = (
  node: OrganizationApiLocationNode,
  draftId: string,
  fallbackStatus: OrganizationState,
  clientId: string,
): OrganizationBranch => {
  const branchId = node.id ?? node._id;

  return {
    ...mapBaseNode(node, draftId, fallbackStatus),
    icon: SVG.filterSidebarRestaurantIcon,
    isClient: false,
    isBranch: true,
    isSubBranch: false,
    clientId,
    subBranches: (node.subBranches ?? []).map((subBranch) =>
      mapSubBranch(subBranch, draftId, fallbackStatus, clientId, branchId),
    ),
  };
};

const mapRoot = (
  node: OrganizationApiLocationNode,
  parentDraftId: string,
): OrganizationRoot => {
  const draftId = node.draftId ?? parentDraftId;
  const clientId = node.id ?? node._id;
  const fallbackStatus: OrganizationState =
    node.status === "draft" || !/^[0-9a-fA-F]{24}$/.test(clientId)
      ? "draft"
      : (node.status ?? "draft");

  return {
    ...mapBaseNode(node, draftId, fallbackStatus),
    isClient: true,
    isBranch: false,
    isSubBranch: false,
    branches: (node.branches ?? []).map((branch) =>
      mapBranch(branch, draftId, fallbackStatus, clientId),
    ),
  };
};

const findOrganizationNode = (
  organizations: OrganizationRoot[],
  nodeId: string,
): OrganizationNode | null => {
  for (const organization of organizations) {
    if (organization.id === nodeId) return organization;

    for (const branch of organization.branches) {
      if (branch.id === nodeId) return branch;

      const subBranch = branch.subBranches.find((item) => item.id === nodeId);
      if (subBranch) return subBranch;
    }
  }

  return null;
};

const dedupeRowsById = (
  rows: OrganizationTableRow[],
): OrganizationTableRow[] => {
  const seen = new Set<string>();

  return rows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};

function OrganizationsScreenContextProvider({ children }: Props) {
  const [selectedTab, setSelectedTab] =
    useState<OrganizationsScreenTypes>("organizationsList");
  const [isAllOrganizationsSelected, setIsAllOrganizationsSelected] =
    useState(true);
  const [selectedSideFilterOrganization, setSelectedSideFilterOrganization] =
    useState<OrganizationNode | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedOrganizationTableRowIds, setSelectedOrganizationTableRowIds] =
    useState<Array<string | number>>([]);
  const [isDefaultParentExpanded, setIsDefaultParentExpanded] = useState(true);

  const { data: organizationDrafts } = useQuery({
    queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
    queryFn: handleGetOrganizationDraftsService,
  });

  const organizations = useMemo(
    () =>
      (organizationDrafts ?? []).flatMap((draft) =>
        (draft.clients ?? []).map((client) => mapRoot(client, draft.draftId)),
      ),
    [organizationDrafts],
  );

  useEffect(() => {
    if (!organizations.length) {
      setSelectedSideFilterOrganization(null);
      return;
    }

    setSelectedSideFilterOrganization((current) =>
      current
        ? (findOrganizationNode(organizations, current.id) ?? organizations[0])
        : organizations[0],
    );
  }, [organizations]);

  useEffect(() => {
    setSelectedOrganizationTableRowIds([]);
  }, [isAllOrganizationsSelected, selectedSideFilterOrganization?.id]);

  const organizationRows = useMemo<OrganizationTableRow[]>(() => {
    const rows: OrganizationTableRow[] = isAllOrganizationsSelected
      ? organizations.map((organization) => ({
          ...organization,
          organizationLevel: "network" as const,
        }))
      : selectedSideFilterOrganization?.isClient
        ? [
            {
              ...selectedSideFilterOrganization,
              organizationLevel: "network" as const,
            },
            ...selectedSideFilterOrganization.branches.map((branch) => ({
              ...branch,
              organizationLevel: "branch" as const,
            })),
          ]
        : selectedSideFilterOrganization?.isBranch
          ? [
              {
                ...selectedSideFilterOrganization,
                organizationLevel: "branch" as const,
              },
              ...selectedSideFilterOrganization.subBranches.map(
                (subBranch) => ({
                  ...subBranch,
                  organizationLevel: "subBranch" as const,
                }),
              ),
            ]
          : selectedSideFilterOrganization?.isSubBranch
            ? [
                {
                  ...selectedSideFilterOrganization,
                  organizationLevel: "subBranch" as const,
                },
              ]
            : [];
    const uniqueRows = dedupeRowsById(rows);

    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return uniqueRows;

    return uniqueRows.filter((row) =>
      row.name.toLowerCase().includes(normalizedSearch),
    );
  }, [
    isAllOrganizationsSelected,
    organizations,
    searchText,
    selectedSideFilterOrganization,
  ]);

  return (
    <OrganizationsScreenContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        isAllOrganizationsSelected,
        setIsAllOrganizationsSelected,
        organizations,
        selectedSideFilterOrganization,
        setSelectedSideFilterOrganization,
        searchText,
        setSearchText,
        selectedOrganizationTableRowIds,
        setSelectedOrganizationTableRowIds,
        organizationRows,
        isDefaultParentExpanded,
        setIsDefaultParentExpanded,
      }}
    >
      {children}
    </OrganizationsScreenContext.Provider>
  );
}

export default OrganizationsScreenContextProvider;

export const useOrganizationsScreenContext = () => {
  const context = React.useContext(OrganizationsScreenContext);
  if (context === null) {
    throw new Error(
      "useOrganizationsScreenContext must be used within an OrganizationsScreenContextProvider",
    );
  }
  return context;
};
